import React, { useMemo, useState } from "react";
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import { FiChevronLeft, FiChevronRight, FiSearch } from "react-icons/fi";
import { CustomTooltip } from "../custom-tooltip";

export type VeiculoData = {
  veiculo: string;
  valor: number;
};

export type RadarChartVeiculosProps = {
  titulo: string;
  subtitulo: string;
  name: string;
  dados: VeiculoData[];
};

const ITEMS_PER_PAGE = 20;
const MAX_RADAR_ITEMS = 100;

const RadarChartVeiculos: React.FC<RadarChartVeiculosProps> = ({
  titulo,
  subtitulo,
  name,
  dados,
}) => {
  const [currentPage, setCurrentPage] = useState(0);

  const totalPages = Math.ceil(dados.length / ITEMS_PER_PAGE);

  const paginatedData = useMemo(() => {
    const start = currentPage * ITEMS_PER_PAGE;
    const end = start + ITEMS_PER_PAGE;
    return dados.slice(start, end);
  }, [dados, currentPage]);

  const handlePrevPage = () =>
    setCurrentPage((prev) => Math.max(prev - 1, 0));

  const handleNextPage = () =>
    setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1));

  const renderPagination = () => {
    if (dados.length <= ITEMS_PER_PAGE) return null;

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginTop: 16,
          gap: 16,
        }}
      >
        <button
          onClick={handlePrevPage}
          disabled={currentPage === 0}
          style={{
            backgroundColor: currentPage === 0 ? "#d3d3d3" : "#15325C",
            border: "none",
            padding: "10px 12px",
            borderRadius: 8,
            cursor: currentPage === 0 ? "not-allowed" : "pointer",
            color: "white",
            fontSize: 18,
          }}
        >
          <FiChevronLeft />
        </button>

        <span
          style={{
            fontSize: 16,
            fontWeight: 600,
            color: "#15325C",
            minWidth: 140,
            textAlign: "center",
          }}
        >
          Página {currentPage + 1} de {totalPages}
        </span>

        <button
          onClick={handleNextPage}
          disabled={currentPage === totalPages - 1}
          style={{
            backgroundColor:
              currentPage === totalPages - 1 ? "#d3d3d3" : "#15325C",
            border: "none",
            padding: "10px 12px",
            borderRadius: 8,
            cursor:
              currentPage === totalPages - 1 ? "not-allowed" : "pointer",
            color: "white",
            fontSize: 18,
          }}
        >
          <FiChevronRight />
        </button>
      </div>
    );
  };

  const renderRadarChart = () => (
    <ResponsiveContainer width="100%" height={420}>
      <RadarChart cx="50%" cy="50%" outerRadius="80%" data={paginatedData}>
        <PolarGrid />
        <PolarAngleAxis
          dataKey="veiculo"
          tick={{ fontSize: 12 }}
          tickLine={false}
        />
        <PolarRadiusAxis tick={false} axisLine={false} />
        <Radar
          name={name}
          dataKey="valor"
          stroke="#15325C"
          fill="#15325C"
          fillOpacity={0.6}
          isAnimationActive
        />
        <Tooltip content={<CustomTooltip labelText={name} />} />
      </RadarChart>
    </ResponsiveContainer>
  );

  const renderBarChart = () => (
    <ResponsiveContainer width="100%" height={420}>
      <BarChart
        data={paginatedData}
        layout="vertical"
        margin={{ top: 20, right: 30, left: 30, bottom: 5 }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis type="number" />
        <YAxis
          type="category"
          dataKey="veiculo"
          width={200}
          tick={{ fontSize: 12 }}
        />
        <Tooltip content={<CustomTooltip labelText={name} />} />
        <Bar
          dataKey="valor"
          name={name}
          fill="#15325C"
          fillOpacity={0.6}
          isAnimationActive
        />
      </BarChart>
    </ResponsiveContainer>
  );

  const renderChart = () => {
    if (dados.length <= MAX_RADAR_ITEMS) {
      return renderRadarChart();
    }
    return renderBarChart();
  };

  return (
    <div
      style={{
        width: "100%",
        minHeight: 520,
        maxWidth: 600,
        margin: "0 auto",
        padding: "16px 4px",
        fontFamily: "'Segoe UI', Roboto, sans-serif",
      }}
    >
      <h2 style={{ marginBottom: 4, color: "#1f4680", fontSize: 20 }}>
        {titulo}
      </h2>
      <p style={{ marginTop: 0, color: "#555", fontSize: 14 }}>
        {subtitulo}
      </p>

      {dados.length === 0 ? (
        <div
          style={{
            height: 420,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            color: "#888",
            fontStyle: "italic",
          }}
        >
          <FiSearch size={48} color="#ccc" />
          Nenhum dado disponível para exibir.
        </div>
      ) : (
        <>
          {renderChart()}
          {renderPagination()}
        </>
      )}
    </div>
  );
};

export default RadarChartVeiculos;
